import type { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { platformCopy } from "./catalog";
import type { ApplicationSubmissionCapabilities, ApplicationSubmissionPayload, ApplicationSubmissionReceipt, AwsEntitlementOption, CreateRequestPayload, PendingSubmission, PortalUser, RequestDetail, RequestSummary, RuntimeConfig, SubmissionLookup } from "./types";

export class PortalApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly fields: Record<string, string[]> = {}, public readonly retryAfterSeconds?: number) { super(message); this.name = "PortalApiError"; }
}

const demoAwsEntitlements: AwsEntitlementOption[] = [
  { accountKey: "martrust-dev", accountName: "martrust-dev", environment: "development", permissionScope: "ReadOnlyAccess" },
  { accountKey: "martrust-dev1", accountName: "martrust-dev1", environment: "development", permissionScope: "AWS-Dev-Admin" },
];

const demoRequests: RequestSummary[] = [
  {
    id: "AAR-2026-0081",
    kind: "admin",
    title: "Azure · SUB-CSP-DataPlatform-DEV",
    status: "pending_data_owner",
    currentApproverName: "Data Platform Owner",
    submittedAt: "2026-08-12T09:30:00.000Z",
    updatedAt: "2026-08-12T10:12:00.000Z",
  },
  {
    id: "AAR-2026-0074",
    kind: "applications",
    title: "Power BI Pro, Visio",
    status: "fulfilment_in_progress",
    submittedAt: "2026-08-08T07:42:00.000Z",
    updatedAt: "2026-08-11T13:18:00.000Z",
    rtTicketId: "29104216",
    rtTicketUrl: "https://rt.marcura.com/Ticket/Display.html?id=29104216",
    rtStatus: "open",
  },
];

export class PortalApi {
  private get submissionCacheKey() { return `access-hub-submission-v2:${this.account?.homeAccountId}:${this.account?.tenantId}`; }
  /** Only correlation metadata is persisted; business text remains server-side/in memory. */
  pendingSubmission(): PendingSubmission | null {
    if (this.config.demoMode) return null;
    const saved = sessionStorage.getItem(this.submissionCacheKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as PendingSubmission;
        if (parsed.protocol === 2 && typeof parsed.key === "string" && typeof parsed.fingerprint === "string") return parsed;
      } catch { throw new Error("The saved submission reference could not be read. Please contact IT before starting another request."); }
      throw new Error("The saved submission reference is invalid. Please contact IT before starting another request.");
    }
    const prefix = `access-hub-submit:${this.account?.homeAccountId}:`;
    for (let index = 0; index < sessionStorage.length; index++) {
      const name = sessionStorage.key(index);
      if (name?.startsWith(prefix)) {
        const key = sessionStorage.getItem(name);
        if (key) return { protocol: 1, key, fingerprint: name.slice(prefix.length) };
      }
    }
    return null;
  }
  private rememberSubmission(receipt: ApplicationSubmissionReceipt) {
    const pending = this.pendingSubmission();
    if (pending?.protocol === 2) sessionStorage.setItem(this.submissionCacheKey, JSON.stringify({ ...pending, parentId: receipt.parentId }));
  }
  finishSubmissionReference(parentId: string) {
    const pending = this.pendingSubmission();
    if (pending?.protocol === 2 && pending.parentId === parentId) sessionStorage.removeItem(this.submissionCacheKey);
  }
  async applicationSubmissionCapabilities(): Promise<ApplicationSubmissionCapabilities> {
    const disabled: ApplicationSubmissionCapabilities = { schemaVersion: 2, enabled: false, maxItems: 0, eligibleApplicationIds: [] };
    if (this.config.demoMode) return disabled;
    try { return await this.request<ApplicationSubmissionCapabilities>("/capabilities/application-submissions"); }
    catch (error) { if (error instanceof PortalApiError && error.status === 404) return disabled; throw error; }
  }
  async createApplicationSubmission(payload: ApplicationSubmissionPayload, signal?: AbortSignal): Promise<ApplicationSubmissionReceipt> {
    signal?.throwIfAborted();
    const fingerprint = [...new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(payload))))].map(value => value.toString(16).padStart(2, "0")).join("");
    signal?.throwIfAborted();
    const existing = this.pendingSubmission();
    if (existing && (existing.protocol !== 2 || existing.fingerprint !== fingerprint)) throw new Error("Resolve your earlier submission before starting a different one. Your earlier request may already have been accepted.");
    const pending: PendingSubmission = existing || { protocol: 2, key: crypto.randomUUID(), fingerprint };
    sessionStorage.setItem(this.submissionCacheKey, JSON.stringify(pending));
    try {
      const receipt = await this.request<ApplicationSubmissionReceipt>("/application-submissions", { method: "POST", headers: { "Idempotency-Key": pending.key }, body: JSON.stringify(payload), signal });
      this.rememberSubmission(receipt);
      return receipt;
    } catch (error) {
      // The v2 contract guarantees these two responses precede durable intake.
      // All uncertain/conflicting responses keep the original protocol/key.
      if (error instanceof PortalApiError && [400, 413].includes(error.status) && !existing?.parentId) sessionStorage.removeItem(this.submissionCacheKey);
      throw error;
    }
  }
  async getApplicationSubmission(id: string, signal?: AbortSignal): Promise<ApplicationSubmissionReceipt> {
    return this.request<ApplicationSubmissionReceipt>(`/application-submissions/${encodeURIComponent(id)}`, { signal });
  }
  async resolvePendingSubmission(signal?: AbortSignal): Promise<SubmissionLookup> {
    const pending = this.pendingSubmission();
    if (!pending) throw new Error("There is no saved pending submission for this account.");
    const result = await this.request<SubmissionLookup>(`/application-submissions/status?protocol=${pending.protocol}`, { signal, headers: { "Idempotency-Key": pending.key } });
    if (result.schemaVersion === 2) this.rememberSubmission(result);
    else sessionStorage.removeItem(`access-hub-submit:${this.account?.homeAccountId}:${pending.fingerprint}`);
    return result;
  }
  async manageApplicationSubmission(id: string, action: "retry-preflight" | "withdraw-preparation" | "resume-creation", value: { eventId: string; expectedVersion: number; reason?: string }, signal?: AbortSignal): Promise<ApplicationSubmissionReceipt> {
    return this.request<ApplicationSubmissionReceipt>(`/application-submissions/${encodeURIComponent(id)}/${action}`, { method: "POST", body: JSON.stringify(value), signal });
  }
  async chatgptReadiness() { return this.request<{ready: boolean; groupVerified: boolean; automationEnabled: boolean; expiryEnabled: boolean; membershipWrites: number}>("/portal-management/chatgpt-readiness"); }
  async listAzureSubscriptions(): Promise<import("./types").AzureSubscriptionOption[]> {
    if (this.config.demoMode) return [
      { subscriptionId: "7aea2af2-3f09-4569-a667-922a7c4402da", subscriptionName: "SUB-CSP-M1-UAT" },
      { subscriptionId: "086777ea-f992-4ddd-9f06-7df49c031458", subscriptionName: "SUB-CSP-M1-CUAT" },
    ];
    return (await this.request<{subscriptions: import("./types").AzureSubscriptionOption[]}>("/catalog/azure-subscriptions")).subscriptions;
  }
  async teamsDelivery() { return this.request<import("./TeamsDelivery").TeamsDeliveryReport>("/portal-management/teams-delivery"); }
  async operationsReadiness() { return this.request<{ready: boolean; mappingsVerified: number; automationEnabled: boolean; membershipWrites: number}>("/portal-management/operations-readiness"); }
  async listOperationsRoles(): Promise<import("./types").OperationsRoleOption[]> {
    if (this.config.demoMode) return ["prod", "dev"].flatMap((environment) => ["Transfer.Ops", "Payroll.Ops", "Treasury.Ops", "cs.read"].map((roleValue) => ({ environment: environment as "prod" | "dev", roleValue })));
    return (await this.request<{roles: import("./types").OperationsRoleOption[]}>("/catalog/operations-roles")).roles;
  }
  async adminAudit(cursor?: string) { return this.request<{records: import("./types").AuditRecord[]; nextCursor: string | null}>(`/portal-management/audit${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`); }
  async adminUsers() { return this.request<{users: import("./types").AdminUser[]; changes: Array<{actorEmail: string; occurredAt: string; target: string}>}>("/portal-management/users"); }
  async saveAdminUser(value: {identity: string; role: string; enabled: boolean; expectedVersion: number}) { return this.request("/portal-management/users", {method: "PUT", body: JSON.stringify(value)}); }
  async adminRouting() { return this.request<{routes: import("./types").AdminRoute[]; changes: Array<{actorEmail: string; occurredAt: string; target: string}>}>("/portal-management/routing"); }
  async saveAdminRouting(value: Record<string, unknown>) { return this.request("/portal-management/routing", {method: "PUT", body: JSON.stringify(value)}); }
  constructor(
    private readonly config: RuntimeConfig,
    private readonly msal: PublicClientApplication | null,
    private readonly account: AccountInfo | null,
  ) {}

  async me(): Promise<PortalUser> {
    if (this.config.demoMode) {
      return { id: "demo-user", email: "a.sanyal@marcura.com", displayName: "Alok Sanyal" };
    }
    return this.request<PortalUser>("/me");
  }

  async listRequests(): Promise<RequestSummary[]> {
    if (this.config.demoMode) return demoRequests;
    const response = await this.request<{ requests: RequestSummary[] }>("/requests");
    return response.requests;
  }

  async listAwsEntitlements(): Promise<AwsEntitlementOption[]> {
    if (this.config.demoMode) return demoAwsEntitlements;
    const response = await this.request<{ entitlements: AwsEntitlementOption[] }>("/catalog/aws-entitlements");
    return response.entitlements;
  }

  async listApprovals(): Promise<RequestSummary[]> {
    if (this.config.demoMode) return [];
    return (await this.request<{ requests: RequestSummary[] }>("/approvals")).requests;
  }

  async getRequest(id: string): Promise<RequestDetail> {
    if (this.config.demoMode) throw new Error("Request detail is available for live requests after sign-in.");
    return this.request<RequestDetail>(`/requests/${encodeURIComponent(id)}`);
  }
  async extendAccess(id: string, endDate: string, reason: string, expectedVersion: number, eventId: string): Promise<void> {
    if (this.config.demoMode) throw new Error("Extensions are available for your live temporary access requests.");
    await this.request(`/requests/${encodeURIComponent(id)}/extension`, { method: "POST", body: JSON.stringify({ endDate, reason, expectedVersion, eventId }) });
  }

  async decide(id: string, decision: string, comment: string, expectedVersion: number, eventId: string, reviewerEmail?: string): Promise<void> {
    if (this.config.demoMode) return;
    await this.request(`/requests/${encodeURIComponent(id)}/decision`, { method: "POST", body: JSON.stringify({ decision, comment, expectedVersion, eventId, reviewerEmail: reviewerEmail || undefined }) });
  }

  async createRequest(payload: CreateRequestPayload): Promise<RequestSummary> {
    if (this.config.demoMode) {
      return {
        id: `AAR-DEMO-${String(Date.now()).slice(-5)}`,
        kind: payload.kind,
        title:
          payload.kind === "applications"
            ? payload.applicationSelections.map((item) => item.applicationName).join(", ")
            : `${payload.adminAccess ? platformCopy[payload.adminAccess.platform].name : "Admin / Dev"} · ${payload.adminAccess?.scopeName || payload.adminAccess?.scopeId}`,
        status: "pending_line_manager",
        currentApproverName: "Your line manager",
        submittedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }
    if (this.pendingSubmission()?.protocol === 2) throw new Error("Resolve your grouped submission before starting another request.");
    const digest = [...new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(payload))))].map((v) => v.toString(16).padStart(2, "0")).join("");
    const cacheKey = `access-hub-submit:${this.account?.homeAccountId}:${digest}`;
    const key = sessionStorage.getItem(cacheKey) || crypto.randomUUID();
    sessionStorage.setItem(cacheKey, key);
    const result = await this.request<RequestSummary>("/requests", {
      method: "POST",
      headers: { "Idempotency-Key": key },
      body: JSON.stringify(payload),
    });
    sessionStorage.removeItem(cacheKey);
    return result;
  }

  async submitClarification(requestId: string, comment: string, expectedVersion: number, eventId: string): Promise<void> {
    if (this.config.demoMode) return;
    await this.request(`/requests/${encodeURIComponent(requestId)}/clarification`, {
      method: "POST",
      body: JSON.stringify({ comment, expectedVersion, eventId }),
    });
  }

  private async request<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
    const msal = this.msal, account = this.account;
    if (!msal || !account) throw new Error("You must sign in before using the portal.");
    const controller = new AbortController();
    const abort = () => controller.abort(init.signal?.reason);
    if (init.signal?.aborted) abort();
    else init.signal?.addEventListener("abort", abort, { once: true });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        reject(new Error("The request timed out. Please retry. If it continues, sign out and sign in again."));
        controller.abort();
      }, 35_000);
    });
    let onAbort: () => void = () => undefined;
    const cancelled = new Promise<never>((_, reject) => {
      onAbort = () => reject(controller.signal.reason || new DOMException("Request cancelled", "AbortError"));
      if (controller.signal.aborted) onAbort();
      else controller.signal.addEventListener("abort", onAbort, { once: true });
    });
    const operation = async (): Promise<T> => {
    controller.signal.throwIfAborted();
    const token = await msal.acquireTokenSilent({
      account,
      scopes: [this.config.apiScope],
    });
    // A token acquired after the deadline must not start a late request/write.
    controller.signal.throwIfAborted();
    const response = await fetch(`${this.config.apiBaseUrl}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.accessToken}`,
        ...init.headers,
      },
    });
    if (!response.ok) {
      const detail = (await response.json().catch(() => null)) as { error?: string; fields?: Record<string, unknown>; retryAfterSeconds?: number } | null;
      const fields = Object.fromEntries(Object.entries(detail?.fields || {}).filter((entry): entry is [string, string[]] => Array.isArray(entry[1]) && entry[1].every(value => typeof value === "string")));
      const seconds = detail?.retryAfterSeconds || Number(response.headers?.get("Retry-After")) || undefined;
      throw new PortalApiError(detail?.error || `Request failed (${response.status}).`, response.status, fields, seconds);
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
    };
    try { return await Promise.race([operation(), deadline, cancelled]); }
    finally { clearTimeout(timer); init.signal?.removeEventListener("abort", abort); controller.signal.removeEventListener("abort", onAbort); }
  }
}
